<script setup lang="ts">
import {defineAsyncComponent, onBeforeMount, onUnmounted, ref, Ref, watch, watchEffect} from "vue";

import DomainBaseEntities from "@/utils/constants/DomainBaseEntities.ts";
import {AxiosRequestConfig} from "axios";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import EmbeddedViewUtils from "@/utils/editView/editScreens/EmbeddedViewUtils.ts";
import ReportViewerPlace from "@/utils/presenters/ReportViewerPlace.ts";
import ReportLauncherPlace from "@/utils/presenters/ReportLauncherPlace.ts";
import {BlockProps, ReportPopupContainer} from "@/utils/presenters/interfaces.ts";
import CustomRepLaunchType from "@/utils/constants/CustomRepLaunchType.ts";
import {WatchStopHandle} from "@vue/runtime-core";
import {NamaNavigationArgs, NamaVueWidget} from "@/utils/common/NamaVueWidget.ts";
import ReportMetadataContainer = namaTypes.ReportMetadataContainer;
import ByIdRequest = namaTypes.ByIdRequest;
import EditScreenReport = namaTypes.EditScreenReport;
import ListScreenLayout = namaTypes.ListScreenLayout;

const props = defineProps<BlockProps>();
let block = props.block as EditScreenReport;
let placeCreated = ref(false);
const reportMetadata: Ref<ReportMetadataContainer | undefined> = ref();
let launchType = block?.reportInfo?.launchType || CustomRepLaunchType.ParamsInPopup;
const popupContainer: ReportPopupContainer = {presenter: props.presenter, block: block, currentMode:ref([CustomRepLaunchType.ParamsInNewPage, CustomRepLaunchType.ParamsInPopup].includes(launchType) ? "launch" : "view")};
onBeforeMount(async () => {
  if (props.collapsed)
    return;
  await start();
});
const emit = defineEmits<{
  (e: 'navigateToNext', args: NamaNavigationArgs): void;
  (e: 'onListScreenLayoutFetched', layout: ListScreenLayout): void;
  (e: 'discussionAdded', args: NamaVueWidget): void;
  (e: 'handleHide'): void;
}>();

watch(() => props.collapsed, async () => {
  if (props.collapsed)
    return;
  await start();
});
let unregisterWatchEffect: WatchStopHandle | undefined;
const viewer = ref(null);
const launcher = ref(null);
async function start() {
  if (reportMetadata.value) {
    popupContainer.place = EmbeddedViewUtils.createEmbeddedReportPlace(popupContainer.block?.reportInfo, reportMetadata.value, props.presenter.dispatcher);
  } else {
    let request: ByIdRequest = {
      entityType: DomainBaseEntities.ReportDefinition,
      id: popupContainer.block?.reportInfo?.reportId
    };
    let settings: AxiosRequestConfig = {
      data: NamaCommon.stringify(request)
    };
    let response = await NamaCommon.namaFetch(NamaURLS.FetchReportMetadata, settings);
    if (response && response.result && response.result.succeded) {
      reportMetadata.value = response.data;
      if (!unregisterWatchEffect) {
        unregisterWatchEffect = watchEffect(() => {
          constructPlaceFromParams();
          //we access viewer and launcher in a setTimeout callback to avoid watching these refs
          //it will lead to infinite refresh cycles
          setTimeout(() => {
            if (viewer.value)
              viewer.value.rerun(popupContainer.place);
            else if (launcher.value)
              launcher.value.rerun(popupContainer.place);
          });
        });
      } else {
        constructPlaceFromParams();
      }
    }
  }
  placeCreated.value = true;
}

function constructPlaceFromParams() {
  popupContainer.place = EmbeddedViewUtils.createEmbeddedReportPlace(popupContainer.block?.reportInfo, reportMetadata.value, props.presenter.dispatcher);
}

onUnmounted(() => {
  if (unregisterWatchEffect)//was created in an async-wait block, will not be managed by vue
    unregisterWatchEffect();
})
const AsyncReportLauncher = defineAsyncComponent(() => import("@/views/ReportLauncher.vue"))
const AsyncReportViewer = defineAsyncComponent(() => import("@/views/ReportViewer.vue"))
</script>

<template>
  <div v-if="placeCreated" class="tw-h-full">
    <div v-if="popupContainer.currentMode.value === 'view' && popupContainer.place instanceof ReportViewerPlace" class="tw-h-full">
      <AsyncReportViewer :popupContainer="popupContainer" ref="viewer"/>
    </div>
    <template v-else-if="popupContainer.currentMode.value === 'launch' && popupContainer.place instanceof ReportLauncherPlace">
      <AsyncReportLauncher :popupContainer="popupContainer" ref="launcher"/>
    </template>
  </div>
</template>

<style scoped>

</style>
